/**
 * The printed medical record ("السجل الطبي").
 *
 * A complete A4 document covering one patient: a cover sheet with a summary,
 * the full visit history, every prescription in date order with its medicines,
 * lab tests and x-rays, and — the part that only exists on paper here — the
 * uploaded result images themselves, laid out on their own pages.
 *
 * There was no such document in the legacy app or in the rebuild. `viewMedicalCV`
 * opened an on-screen modal that could not be printed or handed to anyone, so a
 * patient moving to another clinic had nothing to take with them and a doctor
 * had nothing to attach to a referral.
 *
 * It shares its letterhead vocabulary with `prescriptionPrint.js` — same header
 * templates, same colour handling, same page-break discipline — so the two
 * documents read as coming from the same clinic. What it does not share is the
 * single-page assumption: this one is explicitly paginated, with a section per
 * page group and a repeating running footer.
 */

import { escapeHtml, safeImageSrc, renderHeader, isKnownTemplate, DEFAULT_TEMPLATE } from './prescriptionPrint';
import { isDataUri } from './files';
import {
  iconChip,
  sectionHeading,
  statTile,
  statusPill,
  patientHero,
  emptyNote,
} from './medicalRecordDesign';

/** The colour each kind of content is keyed to, throughout the document. */
const ACCENT = {
  visits: '#0891b2',
  services: '#0284c7',
  prescriptions: '#7c3aed',
  medicines: '#0d9488',
  labs: '#9333ea',
  xrays: '#d97706',
  attachments: '#e11d48',
  notes: '#b45309',
};

const DEFAULT_PRIMARY = '#06b6d4';
const DEFAULT_SECONDARY = '#a855f7';

/** Images per page in the attachments section. Two fit A4 at a readable size. */
const IMAGES_PER_PAGE = 2;

const arabicDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return escapeHtml(value);
  return date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
};

const shortDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return escapeHtml(value);
  return date.toLocaleDateString('ar-EG');
};

/**
 * Every result file attached anywhere in a prescription, with its context.
 *
 * The file shapes are the ones `collectResultFiles` untangles for the screen:
 * a legacy single `resultUrl` string, and a `results` array of `{ data, name }`
 * objects. The payload key is **`data`** — an earlier version of this collector
 * looked for `dataUri` and so found nothing at all, which would have printed a
 * record with an empty attachments section however many files were uploaded.
 *
 * Each attachment carries everything needed to caption it on paper: its own
 * label, the test or x-ray it belongs to, the doctor's comment, the visit's
 * diagnosis and date, and who prescribed it. A printed image with no caption is
 * not a medical record, it is a photograph.
 */
export function collectRecordAttachments(prescriptions = []) {
  const out = [];

  for (const prescription of prescriptions) {
    const context = {
      date: prescription.date,
      prescriptionId: prescription.id,
      diagnosis: prescription.diagnosis || '',
      doctorName:
        prescription.PrescribingDoctor?.name || prescription.doctorName || '',
    };

    const push = (item, kind, itemIndex) => {
      // Same precedence as collectResultFiles: the legacy single URL first,
      // then the modern array.
      const files = [];
      if (item?.resultUrl) files.push({ data: item.resultUrl, name: 'نتيجة سابقة' });
      if (Array.isArray(item?.results)) files.push(...item.results);
      if (Array.isArray(item?.resultFiles)) files.push(...item.resultFiles);

      files.forEach((file, fileIndex) => {
        const src =
          typeof file === 'string'
            ? file
            : file?.data || file?.dataUri || file?.url || file?.src;
        if (!src) return;

        out.push({
          ...context,
          src,
          kind,
          // The file's own label, falling back to the test it belongs to.
          label: (typeof file === 'object' && (file.name || file.label)) || '',
          itemName: item?.name || '',
          itemNote: item?.notes || item?.note || '',
          doctorComment: item?.doctorComment || '',
          uploadedAt: (typeof file === 'object' && (file.uploadedAt || file.date)) || null,
          mimeType: (typeof file === 'object' && file.type) || '',
          key: `${prescription.id}-${kind}-${itemIndex}-${fileIndex}`,
        });
      });
    };

    (prescription.labTests || []).forEach((item, index) => push(item, 'lab', index));
    (prescription.xrays || []).forEach((item, index) => push(item, 'xray', index));
  }

  return out;
}

/** Split a list into fixed-size chunks — one chunk per printed page. */
export const chunk = (items, size) => {
  if (size <= 0) return [items];
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

/**
 * Summary counts for the cover sheet.
 * Cancelled bookings are excluded from "visits" — a cancelled appointment is
 * not a visit, and counting it inflates a record someone may rely on.
 */
export function summarise({ bookings = [], prescriptions = [], externalRecords = [] }) {
  const externalFileCount = externalRecords.reduce((n, r) => n + (r.files || []).length, 0);
  const completed = bookings.filter((b) => b.status === 'completed');
  const live = bookings.filter((b) => b.status !== 'cancelled');
  const medicines = prescriptions.reduce((n, p) => n + (p.medicines || []).length, 0);
  const labs = prescriptions.reduce((n, p) => n + (p.labTests || []).length, 0);
  const xrays = prescriptions.reduce((n, p) => n + (p.xrays || []).length, 0);

  const dates = [...prescriptions.map((p) => p.date), ...live.map((b) => b.date)]
    .filter(Boolean)
    .sort();

  return {
    visits: live.length,
    completedVisits: completed.length,
    prescriptions: prescriptions.length,
    medicines,
    labs,
    xrays,
    attachments: collectRecordAttachments(prescriptions).length + externalFileCount,
    firstVisit: dates[0] || null,
    lastVisit: dates[dates.length - 1] || null,
    externalRecords: externalRecords.length,
  };
}

/* ========================================================================== */

function visitsTable(bookings) {
  if (bookings.length === 0) return emptyNote('لا توجد زيارات مسجلة.');

  const accent = ACCENT.visits;
  const th = `background:${accent}12;color:${accent};padding:9px 10px;text-align:right;border-bottom:2px solid ${accent}55;font-size:11.5px;font-weight:800`;
  const td = 'padding:8px 10px;border-bottom:1px solid #eef2f7;font-size:12px;color:#0f172a';

  return `
    <div class="rec-block" style="border:1px solid #e2e8f0;border-radius:12px;overflow:hidden">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr>
            <th style="${th};width:34px;text-align:center">#</th>
            <th style="${th}">التاريخ</th>
            <th style="${th}">الوقت</th>
            <th style="${th}">الطبيب</th>
            <th style="${th}">العيادة</th>
            <th style="${th}">النوع</th>
            <th style="${th}">الحالة</th>
          </tr>
        </thead>
        <tbody>
          ${bookings
            .map(
              (booking, index) => `
          <tr${index % 2 ? ' style="background:#fbfdfe"' : ''}>
            <td style="${td};text-align:center;color:${accent};font-weight:800">${index + 1}</td>
            <td style="${td};font-weight:700">${shortDate(booking.date)}</td>
            <td style="${td};color:#475569" dir="ltr">${escapeHtml(booking.time)}</td>
            <td style="${td}">${escapeHtml(booking.doctorName || '—')}</td>
            <td style="${td};color:#475569">${escapeHtml(booking.clinicName || '—')}</td>
            <td style="${td}">${
              booking.type === 'followup'
                ? '<span style="color:#0d9488;font-weight:700">متابعة</span>'
                : '<span style="color:#0891b2;font-weight:700">كشف</span>'
            }</td>
            <td style="${td}">${statusPill(booking.status)}</td>
          </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>`;
}

function prescriptionCard(prescription, index) {
  const medicines = prescription.medicines || [];
  const labs = prescription.labTests || [];
  const xrays = prescription.xrays || [];
  const author = prescription.PrescribingDoctor?.name || prescription.doctorName || 'طبيب';

  /** A titled list with its own colour, used for labs and x-rays alike. */
  const requestList = (items, colour, label, icon) =>
    items.length === 0
      ? ''
      : `<div style="margin-top:9px;border-radius:9px;padding:8px 10px;background:${colour}0d;border:1px solid ${colour}2e">
          <p style="margin:0 0 4px;font-size:11px;font-weight:800;color:${colour};display:flex;align-items:center;gap:6px">
            ${iconChip(icon, colour, 20)} ${escapeHtml(label)}
          </p>
          <ul style="margin:0;padding:0 20px;color:#1e293b;font-size:12px">
            ${items
              .map(
                (item) =>
                  `<li style="margin:2px 0">${escapeHtml(item.name)}${
                    item.doctorComment
                      ? ` <span style="color:${colour};font-size:11px">— ${escapeHtml(item.doctorComment)}</span>`
                      : ''
                  }</li>`,
              )
              .join('')}
          </ul>
        </div>`;

  const accent = ACCENT.prescriptions;

  return `
    <div class="rec-block" style="position:relative;border:1px solid #e6e9ef;border-radius:14px;padding:13px 15px 13px 15px;margin-bottom:12px;background:white;box-shadow:0 1px 3px rgba(15,23,42,.06)">
      <div style="position:absolute;top:12px;bottom:12px;right:-1px;width:4px;border-radius:4px;background:linear-gradient(to bottom, ${accent}, ${accent}44)"></div>

      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:4px">
        <div style="display:flex;align-items:center;gap:9px;min-width:0">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:8px;background:${accent}18;color:${accent};font-size:12px;font-weight:900;flex-shrink:0">${index + 1}</span>
          <p style="margin:0;font-weight:900;font-size:14px;color:#0f172a">${escapeHtml(prescription.diagnosis || 'بدون تشخيص مسجل')}</p>
        </div>
        <div style="text-align:left;white-space:nowrap">
          <p style="margin:0;font-size:11px;font-weight:700;color:${accent}">${shortDate(prescription.date)}</p>
          <p style="margin:1px 0 0;font-size:10.5px;color:#94a3b8">${escapeHtml(author)}</p>
        </div>
      </div>

      ${
        medicines.length > 0
          ? `<div style="margin-top:9px;border-radius:9px;padding:8px 10px;background:${ACCENT.medicines}0d;border:1px solid ${ACCENT.medicines}2e">
        <p style="margin:0 0 5px;font-size:11px;font-weight:800;color:${ACCENT.medicines};display:flex;align-items:center;gap:6px">
          ${iconChip('pill', ACCENT.medicines, 20)} الأدوية
        </p>
        <table style="width:100%;border-collapse:collapse;font-size:11.5px">
          ${medicines
            .map(
              (m) => `<tr>
            <td style="padding:2px 0;color:#0f172a;font-weight:800;width:38%">${escapeHtml(m.name)}</td>
            <td style="padding:2px 0;color:#475569">${escapeHtml(
              [m.dosage, m.frequency, m.timing, m.duration].filter(Boolean).join(' · '),
            )}</td>
          </tr>`,
            )
            .join('')}
        </table>
      </div>`
          : ''
      }

      ${requestList(labs, ACCENT.labs, 'التحاليل', 'flask')}
      ${requestList(xrays, ACCENT.xrays, 'الأشعة', 'scan')}

      ${
        prescription.notes
          ? `<div style="margin-top:9px;border-radius:9px;padding:8px 10px;background:${ACCENT.notes}0d;border:1px solid ${ACCENT.notes}33">
        <p style="margin:0;font-size:11.5px;color:#78350f;white-space:pre-line">${escapeHtml(prescription.notes)}</p>
      </div>`
          : ''
      }
    </div>`;
}

/**
 * The attachment pages.
 *
 * Only images are embedded. A PDF result cannot be drawn into a printed page,
 * so it is listed by name instead of silently disappearing — the person holding
 * the printout needs to know a file exists even if they have to open it
 * separately.
 */
function attachmentPages(attachments) {
  if (attachments.length === 0) return '';

  const printable = attachments.filter((a) => {
    const src = String(a.src);
    return isDataUri(src) ? /^data:image\//i.test(src) : Boolean(safeImageSrc(src));
  });
  const unprintable = attachments.filter((a) => !printable.includes(a));

  const kindLabel = (kind) => (kind === 'xray' ? 'أشعة' : 'تحليل');

  const pages = chunk(printable, IMAGES_PER_PAGE)
    .map(
      (group) => `
    <section class="rec-page">
      ${sectionHeading('المرفقات والنتائج', {
        icon: 'image',
        colour: ACCENT.attachments,
        subtitle: 'صور التحاليل والأشعة المرفوعة',
      })}
      ${group
        .map((attachment) => {
          const tone = attachment.kind === 'xray' ? ACCENT.xrays : ACCENT.labs;
          const title = attachment.itemName || attachment.label || kindLabel(attachment.kind);
          // Sub-label only when it says something the title does not.
          const sub = attachment.label && attachment.label !== title ? attachment.label : '';

          const detail = (icon, colour, label, value) =>
            value
              ? `<div style="display:flex;align-items:center;gap:6px;min-width:0">
              ${iconChip(icon, colour, 18)}
              <span style="font-size:10.5px;color:#64748b"><strong style="color:#334155">${escapeHtml(label)}</strong> ${escapeHtml(value)}</span>
            </div>`
              : '';

          return `
      <figure class="rec-block" style="margin:0 0 13px;border:1px solid ${tone}33;border-radius:14px;padding:11px;background:linear-gradient(160deg, ${tone}0a, transparent);box-shadow:0 1px 3px rgba(15,23,42,.05)">
        <figcaption style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:9px">
          <span style="display:flex;align-items:center;gap:8px;min-width:0">
            ${iconChip(attachment.kind === 'xray' ? 'scan' : 'flask', tone, 24)}
            <span style="min-width:0">
              <span style="display:block;font-weight:800;color:#0f172a;font-size:12.5px">${escapeHtml(title)}</span>
              ${sub ? `<span style="display:block;font-size:10.5px;color:#94a3b8">${escapeHtml(sub)}</span>` : ''}
            </span>
          </span>
          <span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:10.5px;font-weight:700;color:${tone};background:${tone}17;border:1px solid ${tone}38;white-space:nowrap">${escapeHtml(kindLabel(attachment.kind))} · ${shortDate(attachment.uploadedAt || attachment.date)}</span>
        </figcaption>

        <img src="${safeImageSrc(attachment.src) || escapeHtml(attachment.src)}" alt="${escapeHtml(title)}" style="width:100%;max-height:300px;object-fit:contain;background:#f8fafc;border-radius:9px;border:1px solid #eef2f7">

        <div style="display:flex;flex-wrap:wrap;gap:10px;margin-top:8px;padding-top:8px;border-top:1px dashed ${tone}33">
          ${detail('calendar', '#64748b', 'تاريخ الزيارة:', shortDate(attachment.date))}
          ${detail('user', '#64748b', 'الطبيب:', attachment.doctorName)}
          ${detail('clipboard', '#64748b', 'التشخيص:', attachment.diagnosis)}
        </div>

        ${
          attachment.itemNote
            ? `<p style="margin:7px 0 0;font-size:11px;color:#475569">ملاحظة الطلب: ${escapeHtml(attachment.itemNote)}</p>`
            : ''
        }
        ${
          attachment.doctorComment
            ? `<p style="margin:7px 0 0;font-size:11px;color:#475569;background:white;border-right:3px solid ${tone};border-radius:6px;padding:6px 9px">تعليق الطبيب: ${escapeHtml(attachment.doctorComment)}</p>`
            : ''
        }
      </figure>`;
        })
        .join('')}
    </section>`,
    )
    .join('');

  const missing =
    unprintable.length > 0
      ? `
    <section class="rec-page">
      ${sectionHeading('مرفقات غير قابلة للطباعة', {
        icon: 'paperclip',
        colour: '#64748b',
        subtitle: 'ملفات مرفقة بالسجل لكنها ليست صوراً',
      })}
      <ul class="rec-block" style="margin:0;padding:12px 32px;font-size:12px;color:#1e293b;border:1px dashed #cbd5e1;border-radius:10px">
        ${unprintable
          .map(
            (a) =>
              `<li style="margin:3px 0">${escapeHtml(kindLabel(a.kind))}: ${escapeHtml(a.itemName)} — ${shortDate(a.date)}</li>`,
          )
          .join('')}
      </ul>
    </section>`
      : '';

  return pages + missing;
}

/** How a patient-uploaded document describes itself. */
export const EXTERNAL_TYPE_LABEL = {
  lab: 'تحليل',
  xray: 'أشعة',
  prescription: 'روشتة',
  report: 'تقرير طبي',
  vaccination: 'تطعيم',
  surgery: 'عملية',
  other: 'مستند',
};

const EXTERNAL_TYPE_ICON = {
  lab: 'flask',
  xray: 'scan',
  prescription: 'file-text',
  report: 'clipboard',
  vaccination: 'heart',
  surgery: 'activity',
  other: 'paperclip',
};

/**
 * Documents the patient brought in from elsewhere.
 *
 * Printed as their own section, clearly marked as external, with every field
 * the patient supplied — the document's own date, the outside doctor and
 * facility, their notes, the visit it was linked to, and the images themselves.
 * Presenting an outside scan as though this clinic had ordered it would
 * misrepresent the record.
 */
/** How each institution service reads in the record. */
const SERVICE_LABEL = {
  surgery: 'عملية جراحية',
  lab: 'تحليل طبي',
  radiology: 'أشعة',
  inpatient: 'حجز داخلي',
  icu: 'رعاية مركزة',
  physiotherapy: 'علاج طبيعي',
};

// Every name here must exist in ICON_PATHS — iconChip falls back silently to
// 'activity' for anything it does not know, which looks like a design choice
// rather than a missing glyph.
const SERVICE_ICON = {
  surgery: 'scissors',
  lab: 'flask',
  radiology: 'scan',
  inpatient: 'bed',
  icu: 'heart',
  physiotherapy: 'activity',
};

/**
 * Services the patient had at a hospital, lab or centre.
 *
 * These belong in the record for the same reason the externally uploaded
 * documents do: a scan is part of a medical history whether or not a doctor on
 * this platform ordered it. Without this section a patient who had their
 * surgery through the app and their follow-up scan through the same hospital
 * would print a record showing only one of them.
 *
 * Cancelled bookings are excluded — an appointment that never happened is not
 * history, and listing it invites a clinician to read it as one.
 */
function serviceBookingsSection(bookings) {
  const done = (bookings || []).filter(
    (booking) => booking && booking.status !== 'cancelled',
  );
  if (done.length === 0) return '';

  const ordered = [...done].sort((a, b) =>
    String(b.date || '').localeCompare(String(a.date || '')),
  );

  const tone = ACCENT.services;

  const card = (booking) => {
    const label = SERVICE_LABEL[booking.category] || booking.category || 'خدمة';
    const icon = SERVICE_ICON[booking.category] || 'stethoscope';

    const images = (booking.attachments || []).filter((file) =>
      /^data:image\//i.test(String(file?.data || '')),
    );
    const others = (booking.attachments || []).filter((file) => !images.includes(file));

    const meta = (key, value) =>
      value
        ? `<span style="font-size:10.5px;color:#64748b"><strong style="color:#334155">${escapeHtml(key)}</strong> ${escapeHtml(String(value))}</span>`
        : '';

    return `
    <div class="rec-block" style="border:1px solid ${tone}2e;border-radius:14px;padding:12px 14px;margin-bottom:12px;background:linear-gradient(160deg, ${tone}09, transparent)">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:6px">
        <div style="display:flex;align-items:center;gap:9px;min-width:0">
          ${iconChip(icon, tone, 26)}
          <p style="margin:0;font-weight:900;font-size:14px;color:#0f172a">${escapeHtml(booking.serviceName || label)}</p>
        </div>
        <span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:10.5px;font-weight:700;color:${tone};background:${tone}17;border:1px solid ${tone}38;white-space:nowrap">${escapeHtml(label)} · ${shortDate(booking.date)}</span>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:12px;margin:6px 0 0">
        ${meta('الجهة:', booking.institutionName)}
        ${booking.isHomeVisit ? meta('الزيارة:', 'منزلية') : ''}
        ${meta('المدة:', booking.estimatedMinutes ? `${booking.estimatedMinutes} دقيقة` : '')}
      </div>

      ${
        booking.resultNotes
          ? `<p style="margin:8px 0 0;font-size:11.5px;color:#475569;white-space:pre-line;background:white;border-right:3px solid ${tone};border-radius:6px;padding:6px 9px">${escapeHtml(booking.resultNotes)}</p>`
          : ''
      }

      ${
        images.length > 0
          ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:9px">${images
              .map(
                (file) =>
                  `<figure style="margin:0;width:150px"><img src="${safeImageSrc(file.data)}" alt="" style="width:100%;border-radius:8px;border:1px solid ${tone}33"><figcaption style="font-size:9.5px;color:#64748b;margin-top:3px;text-align:center">${escapeHtml(file.name || '')}</figcaption></figure>`,
              )
              .join('')}</div>`
          : ''
      }

      ${
        others.length > 0
          ? `<p style="margin:7px 0 0;font-size:10.5px;color:#64748b">مرفقات أخرى: ${others
              .map((file) => escapeHtml(file.name || 'ملف'))
              .join('، ')}</p>`
          : ''
      }
    </div>`;
  };

  return `
    <section class="rec-page">
      ${sectionHeading('خدمات المستشفيات والمراكز', {
        icon: 'building-2',
        colour: ACCENT.services,
        subtitle: `${ordered.length} خدمة مسجلة`,
      })}
      ${ordered.map(card).join('')}
    </section>`;
}

function externalRecordsSection(records) {
  if (!records || records.length === 0) return '';

  const ordered = [...records].sort((a, b) =>
    String(b.recordDate || b.createdAt || '').localeCompare(String(a.recordDate || a.createdAt || '')),
  );

  const card = (record) => {
    const label = EXTERNAL_TYPE_LABEL[record.recordType] || EXTERNAL_TYPE_LABEL.other;
    const icon = EXTERNAL_TYPE_ICON[record.recordType] || EXTERNAL_TYPE_ICON.other;
    const tone = ACCENT.attachments;

    const images = (record.files || []).filter((file) => {
      const src = String(file?.data || file || '');
      return /^data:image\//i.test(src) || Boolean(safeImageSrc(src));
    });
    const others = (record.files || []).filter((file) => !images.includes(file));

    const meta = (label_, value) =>
      value
        ? `<span style="font-size:10.5px;color:#64748b"><strong style="color:#334155">${escapeHtml(label_)}</strong> ${escapeHtml(value)}</span>`
        : '';

    return `
    <div class="rec-block" style="border:1px solid ${tone}2e;border-radius:14px;padding:12px 14px;margin-bottom:12px;background:linear-gradient(160deg, ${tone}09, transparent)">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:6px">
        <div style="display:flex;align-items:center;gap:9px;min-width:0">
          ${iconChip(icon, tone, 26)}
          <p style="margin:0;font-weight:900;font-size:14px;color:#0f172a">${escapeHtml(record.title)}</p>
        </div>
        <span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:10.5px;font-weight:700;color:${tone};background:${tone}17;border:1px solid ${tone}38;white-space:nowrap">${escapeHtml(label)} · ${shortDate(record.recordDate || record.createdAt)}</span>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:12px;margin:6px 0 0">
        ${meta('الطبيب:', record.externalDoctor)}
        ${meta('الجهة:', record.facility)}
        ${meta('مرتبط بزيارة:', record.linkedVisitLabel)}
      </div>

      ${
        record.notes
          ? `<p style="margin:8px 0 0;font-size:11.5px;color:#475569;white-space:pre-line;background:white;border-right:3px solid ${tone};border-radius:6px;padding:6px 9px">${escapeHtml(record.notes)}</p>`
          : ''
      }

      ${images
        .map(
          (file) => `
      <figure class="rec-block" style="margin:10px 0 0">
        <img src="${safeImageSrc(file.data || file) || escapeHtml(file.data || file)}" alt="${escapeHtml(record.title)}" style="width:100%;max-height:300px;object-fit:contain;background:#f8fafc;border-radius:9px;border:1px solid #eef2f7">
        ${file.name ? `<figcaption style="margin:4px 0 0;font-size:10.5px;color:#94a3b8;text-align:center">${escapeHtml(file.name)}</figcaption>` : ''}
      </figure>`,
        )
        .join('')}

      ${
        others.length > 0
          ? `<p style="margin:8px 0 0;font-size:10.5px;color:#94a3b8">${others.length} ملف غير قابل للطباعة (${escapeHtml(
              others.map((f) => f.name || 'ملف').join('، '),
            )})</p>`
          : ''
      }
    </div>`;
  };

  return `
    <section class="rec-page">
      ${sectionHeading('سجلات خارجية أرفقها المريض', {
        icon: 'paperclip',
        colour: ACCENT.attachments,
        subtitle: `${ordered.length} مستند من خارج المنصة`,
      })}
      <p style="margin:0 0 12px;font-size:11px;color:#94a3b8">
        هذه المستندات رفعها المريض بنفسه من جهات خارج المنصة، ولم تصدر عن هذه العيادة.
      </p>
      ${ordered.map(card).join('')}
    </section>`;
}

/* ========================================================================== */

/**
 * Build the printable medical record.
 *
 * @param {object}   args
 * @param {object}   args.patient       { name, code, phone }
 * @param {Array}    [args.bookings]
 * @param {Array}    [args.prescriptions]
 * @param {Array}    [args.externalRecords] documents the patient uploaded themselves
 * @param {object}   [args.doctor]      letterhead owner; falls back to a neutral one
 * @param {string}   [args.issuedBy]    who produced the copy, printed on the cover
 * @returns {string} a complete HTML document
 */
export function buildMedicalRecordHtml({
  patient,
  bookings = [],
  prescriptions = [],
  externalRecords = [],
  // Surgeries, scans, tests and stays booked at an institution. Part of the
  // history whether or not a doctor on this platform ordered them.
  serviceBookings = [],
  doctor = null,
  issuedBy = '',
}) {
  if (!patient?.name) throw new Error('buildMedicalRecordHtml: patient.name is required');

  const primary = doctor?.printColors?.primary || DEFAULT_PRIMARY;
  const secondary = doctor?.printColors?.secondary || DEFAULT_SECONDARY;

  const requested = doctor?.prescriptionStyle || DEFAULT_TEMPLATE;
  const style = isKnownTemplate(requested) ? requested : DEFAULT_TEMPLATE;

  const logo = safeImageSrc(doctor?.logo);
  const avatar = safeImageSrc(doctor?.image);

  const logoBox = logo
    ? `<img src="${logo}" alt="" style="width:76px;height:76px;object-fit:contain;flex-shrink:0">`
    : '';
  const avatarBox = avatar
    ? `<div style="width:68px;height:68px;border-radius:50%;overflow:hidden;border:3px solid rgba(255,255,255,.8);flex-shrink:0"><img src="${avatar}" alt="" style="width:100%;height:100%;object-fit:cover"></div>`
    : '';

  const headerText = (onColour, centred = false) => `
    <div style="text-align:${centred ? 'center' : 'right'}">
      <h1 style="font-size:24px;font-weight:900;color:${onColour ? '#ffffff' : '#0f172a'};margin:0">السجل الطبي</h1>
      <p style="font-size:13px;color:${onColour ? 'rgba(255,255,255,.85)' : '#475569'};margin:4px 0 0">${escapeHtml(patient.name)}</p>
      ${doctor?.name ? `<p style="font-size:11px;color:${onColour ? 'rgba(255,255,255,.65)' : '#94a3b8'};margin:3px 0 0">د/ ${escapeHtml(doctor.name)}</p>` : ''}
    </div>`;

  const header = renderHeader(style, { primary, secondary, avatarBox, logoBox, headerText });

  // Newest first: the most recent visit is what anyone opening the record wants.
  const byDateDesc = (a, b) => String(b.date || '').localeCompare(String(a.date || ''));
  const orderedBookings = [...bookings].sort(byDateDesc);
  const orderedPrescriptions = [...prescriptions].sort(byDateDesc);

  const stats = summarise({ bookings, prescriptions, externalRecords });
  const attachments = collectRecordAttachments(orderedPrescriptions);

  const cover = `
    <section class="rec-page">
      ${patientHero({
        name: patient.name,
        code: patient.code || '',
        phone: patient.phone || '',
        issued: arabicDate(new Date()),
        primary,
        secondary,
      })}

      <div style="display:flex;flex-wrap:wrap;gap:7px;margin-bottom:16px">
        ${statTile({ label: 'زيارة', value: stats.visits, colour: ACCENT.visits, icon: 'calendar' })}
        ${statTile({ label: 'روشتة', value: stats.prescriptions, colour: ACCENT.prescriptions, icon: 'file-text' })}
        ${statTile({ label: 'دواء', value: stats.medicines, colour: ACCENT.medicines, icon: 'pill' })}
        ${statTile({ label: 'تحليل', value: stats.labs, colour: ACCENT.labs, icon: 'flask' })}
        ${statTile({ label: 'أشعة', value: stats.xrays, colour: ACCENT.xrays, icon: 'scan' })}
        ${statTile({ label: 'مرفق', value: stats.attachments, colour: ACCENT.attachments, icon: 'image' })}
      </div>

      <div class="rec-block" style="display:flex;gap:9px;margin-bottom:18px">
        <div style="flex:1;display:flex;align-items:center;gap:9px;border:1px solid #e2e8f0;border-radius:11px;padding:9px 12px">
          ${iconChip('clock', '#64748b', 26)}
          <div>
            <p style="margin:0;font-size:10.5px;color:#94a3b8;font-weight:700">أول زيارة</p>
            <p style="margin:1px 0 0;font-size:12.5px;font-weight:800;color:#0f172a">${arabicDate(stats.firstVisit)}</p>
          </div>
        </div>
        <div style="flex:1;display:flex;align-items:center;gap:9px;border:1px solid #e2e8f0;border-radius:11px;padding:9px 12px">
          ${iconChip('check', ACCENT.medicines, 26)}
          <div>
            <p style="margin:0;font-size:10.5px;color:#94a3b8;font-weight:700">آخر زيارة</p>
            <p style="margin:1px 0 0;font-size:12.5px;font-weight:800;color:#0f172a">${arabicDate(stats.lastVisit)}</p>
          </div>
        </div>
      </div>

      ${sectionHeading('سجل الزيارات', {
        icon: 'calendar',
        colour: ACCENT.visits,
        subtitle: `${stats.visits} زيارة مسجلة`,
      })}
      ${visitsTable(orderedBookings)}
    </section>`;

  const prescriptionsSection =
    orderedPrescriptions.length > 0
      ? `
    <section class="rec-page">
      ${sectionHeading('الروشتات والتشخيصات', {
        icon: 'file-text',
        colour: ACCENT.prescriptions,
        subtitle: `${orderedPrescriptions.length} روشتة، الأحدث أولاً`,
      })}
      ${orderedPrescriptions.map((p, i) => prescriptionCard(p, i)).join('')}
    </section>`
      : `
    <section class="rec-page">
      ${sectionHeading('الروشتات والتشخيصات', {
        icon: 'file-text',
        colour: ACCENT.prescriptions,
      })}
      ${emptyNote('لا توجد روشتات مسجلة.')}
    </section>`;

  const externalSection = externalRecordsSection(externalRecords);
  const servicesSection = serviceBookingsSection(serviceBookings);

  const footerNote = issuedBy
    ? `صادر بواسطة ${escapeHtml(issuedBy)}`
    : 'نسخة من السجل الطبي';

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="utf-8">
<title>السجل الطبي - ${escapeHtml(patient.name)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: 'Noto Kufi Arabic', Tahoma, Arial, sans-serif;
    background: white;
    color: #0f172a;
  }

  /* Each section starts a fresh sheet. This is the difference between this
     document and the prescription: the record is explicitly paginated, so a
     visit table never ends up half on one page and half on the next. */
  .rec-page {
    padding: 14px 34px 20px;
    break-after: page;
    page-break-after: always;
  }
  .rec-page:last-of-type {
    break-after: auto;
    page-break-after: auto;
  }

  /* Cards, figures and table rows stay whole across a page boundary. */
  .rec-block,
  .rec-footer,
  tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* The footer sits in a <tfoot>. That is the one construct browsers both
     repeat on every printed page AND reserve vertical space for; a fixed
     element repeats but paints over the content instead of pushing it down. */
  .rec-sheet { width: 100%; border-collapse: collapse; }
  .rec-sheet > tfoot { display: table-footer-group; }
  .rec-sheet > tbody > tr > td,
  .rec-sheet > tfoot > tr > td { padding: 0; vertical-align: top; }

  .rec-footer {
    border-top: 2px solid ${primary};
    background: white;
    padding: 8px 34px;
    font-size: 11px;
    color: #94a3b8;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  @media print {
    /* Page one is full bleed for the letterhead; every page after it gets a
       top margin so a section that starts mid-document is not pressed against
       the edge of the sheet. */
    @page { size: A4; margin: 14mm 0 0 0; }
    @page :first { margin: 0; }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }

  @media screen {
    .rec-page { border-bottom: 1px dashed #cbd5e1; }
  }
</style>
</head>
<body>
  <table class="rec-sheet">
    <tfoot><tr><td>
      <div class="rec-footer">
        <span>${escapeHtml(patient.name)}${patient.code ? ` — ${escapeHtml(patient.code)}` : ''}</span>
        <span>${footerNote}</span>
        <span>Powered by Capsule Medical System</span>
      </div>
    </td></tr></tfoot>
    <tbody><tr><td>
      ${header}
      ${cover}
      ${prescriptionsSection}
      ${servicesSection}
      ${externalSection}
      ${attachmentPages(attachments)}
    </td></tr></tbody>
  </table>
</body>
</html>`;
}

export default buildMedicalRecordHtml;
